import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { MdDownload, MdEdit } from "react-icons/md";
import { FiTrash2, FiX } from "react-icons/fi";
import { FileUploader, Spinner, RoleValidator } from "../../Components/Common";
import { getProjectDetails } from "../../Services/projectServices";
import { Pagination, MaterialModal, FilePreviewModal } from "./Components";
import { ROLES } from "../../Constants/Strings";
import {
  MATERIAL_COLUMNS,
  MATERIAL_FORM_LABELS,
  MATERIAL_RENDER_FIELDS,
  ADD_MATERIAL_FORM_LABELS,
  ADD_MATERIAL_RENDER_FIELDS,
  TOAST_MESSAGES,
} from "./constants";
import { useMaterials } from "./Hooks/useMaterials";
import { toProperCase } from "../../Utils";
import { sample_material_excel } from "../../Assets";
import { bulkMaterialUpdate } from "../../Services/materialService";
import toast from "react-hot-toast";
import { debounce } from "lodash";

const ManageMaterials = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [projectDetails, setProjectDetails] = useState({});
  const {
    materials,
    setMaterials,
    loading,
    page,
    materialCount,
    setPage,
    deleteMaterial,
    addMaterial,
    updateMaterial,
    uploadExcelFile,
    totalPages,
    fetchMaterials,
    search,
    setSearch,
  } = useMaterials(projectId);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState({});
  const [originalMaterial, setOriginalMaterial] = useState({});
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Fetch project details
  useEffect(() => {
    getProjectDetails(projectId).then((res) => setProjectDetails(res.data));
  }, [projectId]);

  // Debounced live search
  const debouncedFetch = debounce((query) => {
    setPage(1);
    fetchMaterials(query);
    setIsSearching(query.trim() !== "");
  }, 300);

  useEffect(() => {
    debouncedFetch(search);
    return () => debouncedFetch.cancel();
  }, [search]);

  const handleAddMaterial = async () => {
    await addMaterial(selectedMaterial);
    setIsAddModalOpen(false);
    setSelectedMaterial({});
  };

  const handleEditMaterial = async (material_id) => {
    if (JSON.stringify(selectedMaterial) === JSON.stringify(originalMaterial)) {
      toast("No changes detected", { icon: "ℹ️" });
      setIsEditModalOpen(false);
      setSelectedMaterial({});
      setOriginalMaterial({});
      return;
    }

    setIsSubmitting(true);
    await updateMaterial(material_id, selectedMaterial);
    setIsSubmitting(false);
    setIsEditModalOpen(false);
    setSelectedMaterial({});
    setOriginalMaterial({});
  };

  const canMarkDelivered = (() => {
    if (selectedIds.size === 0) return false;
    return materials.some(
      (mat) =>
        selectedIds.has(mat.id) &&
        (mat.material_delivery_status !== "DELIVERED" ||
          !mat.material_delivery_date)
    );
  })();

  const handleSelectAll = () => {
    const currentPageIds = materials.map((m) => m.id);
    const newSelected = new Set(selectedIds);
    const allSelectedOnPage = currentPageIds.every((id) => newSelected.has(id));
    if (allSelectedOnPage) {
      currentPageIds.forEach((id) => newSelected.delete(id));
    } else {
      currentPageIds.forEach((id) => newSelected.add(id));
    }
    setSelectedIds(newSelected);
  };

  const handleSelectOne = (id) => {
    const newSelected = new Set(selectedIds);
    newSelected.has(id) ? newSelected.delete(id) : newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const handleBulkDelivered = async () => {
    setIsSubmitting(true);
    try {
      const today = new Date().toISOString().split("T")[0];
      await bulkMaterialUpdate(projectId, {
        materials: Array.from(selectedIds).map((id) => ({
          id,
          material_delivery_status: "DELIVERED",
          material_delivery_date: today,
        })),
      });

      setMaterials((prev) =>
        prev.map((mat) =>
          selectedIds.has(mat.id)
            ? {
                ...mat,
                material_delivery_status: "DELIVERED",
                material_delivery_date: today,
              }
            : mat
        )
      );
      setSelectedIds(new Set());
      toast.success(TOAST_MESSAGES.BULK_UPDATE_SUCCESS);
    } catch (err) {
      toast.error(TOAST_MESSAGES.BULK_UPDATE_FAIL);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMaterialValue = (mat, key) => {
    if (key === "material_delivery_date") {
      return mat[key] ? new Date(mat[key]).toLocaleDateString() : "-";
    }
    if (key === "file" && mat[key]) {
      return (
        <button
          type="button"
          className="text-blue-600 underline bg-transparent border-none p-0 cursor-pointer"
          onClick={() => setFilePreviewUrl(mat[key])}
        >
          View File
        </button>
      );
    }
    if (key === "quantity") return mat[key] ? parseInt(mat[key]) : "-";
    return mat[key] || "-";
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner size={12} />
      </div>
    );

  return (
    <div className="h-[80%]">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-4">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)}>
            <IoMdArrowRoundBack size={20} className="text-secondary" />
          </button>
          <h1 className="text-lg sm:text-xl font-semibold">
            Manage Materials ({projectDetails?.name})
          </h1>
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center gap-2 sm:gap-3 justify-end">
          {/* Search box (desktop) */}
          <div className="hidden md:flex items-center gap-2 relative">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64 px-4 py-2 rounded-lg border border-gray-300 shadow-sm
                focus:outline-none focus:ring-1 focus:ring-blue-500
                focus:border-blue-500 text-sm"
            />
            <button
              onClick={() => setSearch("")}
              className="absolute top-2 right-2 text-gray-500 hover:text-blue-600"
            >
              {isSearching && <FiX size={20} />}
            </button>
          </div>

          <div className="flex mt-2 items-center gap-2 sm:gap-3">
            <RoleValidator allowedRoles={[ROLES.ADMIN, ROLES.USER]}>
              <a
                href={sample_material_excel}
                download="sample_material.xlsx"
                className="shrink-0"
              >
                <MdDownload
                  size={20}
                  className="text-gray-500 hover:text-blue-600"
                  title="Download Material Sample"
                />
              </a>

              <FileUploader
                accept=".xlsx,.xls"
                singleButton
                btnText="Upload file"
                onUploadClick={uploadExcelFile}
                singleBtnClassName="bg-primary text-white rounded text-sm sm:text-base px-2 py-1.5 sm:px-4 sm:py-2"
              />
              <button
                className="bg-primary text-white rounded text-sm sm:text-base px-2 py-1.5 sm:px-4 sm:py-2 shrink-0"
                onClick={() => setIsAddModalOpen(true)}
              >
                + Add Material
              </button>
            </RoleValidator>
          </div>
        </div>
      </div>

      {/* Search (mobile) */}
      <div className="flex md:hidden items-center justify-end relative mb-4 mr-4">
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-44 px-4 py-2 rounded-lg border border-gray-300 shadow-sm
            focus:outline-none focus:ring-1 focus:ring-blue-500
            focus:border-blue-500 text-sm"
        />
        <button
          onClick={() => setSearch("")}
          className="absolute top-2 right-2 text-gray-500 bg-white pl-2 hover:text-blue-600"
        >
          {isSearching && <FiX size={20} />}
        </button>
      </div>

      {/* Selected Count */}
      {selectedIds.size !== 0 && (
        <div className="flex items-center gap-4 mb-4">
          <div className="mt-2 ml-4 inline-block rounded bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 border border-blue-200">
            Selected {selectedIds.size} of {materialCount}
          </div>
          <button
            title={canMarkDelivered ? "Mark as Delivered" : "Already Delivered"}
            className={`mt-2 rounded text-xs sm:text-xs py-1 px-3 sm:px-4 sm:py-1 shrink-0 ${
              canMarkDelivered
                ? "bg-primary text-white"
                : "bg-gray-300 text-gray-600 cursor-not-allowed"
            }`}
            onClick={handleBulkDelivered}
            disabled={
              isSubmitting ||
              Array.from(selectedIds).every((id) => {
                const mat = materials.find((m) => m.id === id);
                return mat?.material_delivery_status === "DELIVERED";
              })
            }
          >
            Mark as Delivered
          </button>
        </div>
      )}

      {/* Table */}
      <div
        className={`relative ${
          selectedIds.size === 0
            ? "h-[59vh] md:h-[56vh]"
            : "h-[53vh] md:h-[51vh]"
        } overflow-y-auto overflow-x-auto md:overflow-x-hidden m-4`}
      >
        <table className="w-full text-sm text-left text-gray-500 border-collapse">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr>
              <RoleValidator allowedRoles={[ROLES.ADMIN, ROLES.USER]}>
                <th className="px-6 py-3 w-12">
                  <input
                    type="checkbox"
                    checked={
                      materials.length > 0 &&
                      materials.every((m) => selectedIds.has(m.id))
                    }
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="px-6 py-3 w-32">Action</th>
              </RoleValidator>
              {MATERIAL_COLUMNS.map((col) => (
                <th key={col.key} className="px-6 py-3 min-w-[150px]">
                  {toProperCase(col.label)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {materials.length > 0 ? (
              materials.map((mat) => (
                <tr key={mat.id} className="bg-white border-b hover:bg-gray-50">
                  <RoleValidator allowedRoles={[ROLES.ADMIN, ROLES.USER]}>
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(mat.id)}
                        onChange={() => handleSelectOne(mat.id)}
                      />
                    </td>
                    <td className="flex items-center px-6 py-4 gap-4 min-w-[120px]">
                      <MdEdit
                        size={20}
                        className={`${
                          mat.material_delivery_status === "DELIVERED" &&
                          mat.file
                            ? "text-gray-400 cursor-not-allowed"
                            : "cursor-pointer text-primary"
                        }`}
                        title={
                          mat.material_delivery_status === "DELIVERED" &&
                          mat.file
                            ? "Cannot edit delivered material with file"
                            : "Edit"
                        }
                        onClick={() => {
                          if (
                            !(
                              mat.material_delivery_status === "DELIVERED" &&
                              mat.file
                            )
                          ) {
                            setSelectedMaterial(mat);
                            setOriginalMaterial(mat);
                            setIsEditModalOpen(true);
                          }
                        }}
                      />

                      <FiTrash2
                        size={20}
                        className={`cursor-pointer ${
                          mat.material_delivery_status === "DELIVERED"
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-red-500"
                        }`}
                        title={
                          mat.material_delivery_status === "DELIVERED"
                            ? "Cannot delete delivered material"
                            : "Delete"
                        }
                        onClick={() =>
                          mat.material_delivery_status !== "DELIVERED" &&
                          deleteMaterial(mat.id)
                        }
                      />
                    </td>
                  </RoleValidator>

                  {MATERIAL_COLUMNS.map((col) => (
                    <td key={col.key} className="px-6 py-4 min-w-[150px]">
                      {renderMaterialValue(mat, col.key)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={MATERIAL_COLUMNS.length + 2}
                  className="px-6 py-4 text-center text-gray-500"
                >
                  No materials found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end mt-3 pr-4">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      {/* Modals */}
      {isEditModalOpen && (
        <MaterialModal
          title="Edit Material"
          formData={selectedMaterial}
          onChange={(field, value) =>
            setSelectedMaterial((prev) => ({ ...prev, [field]: value }))
          }
          onSave={() => handleEditMaterial(selectedMaterial.id)}
          onClose={() => setIsEditModalOpen(false)}
          isSubmitting={isSubmitting}
          fields={MATERIAL_RENDER_FIELDS}
          labels={MATERIAL_FORM_LABELS}
          disableSave={
            JSON.stringify(selectedMaterial) ===
            JSON.stringify(originalMaterial)
          }
        />
      )}

      {isAddModalOpen && (
        <MaterialModal
          title="Add Material"
          formData={{
            material_name: "",
            specification: "",
            unit: "",
            quantity: "",
          }}
          onChange={(field, value) =>
            setSelectedMaterial((prev) => ({ ...prev, [field]: value }))
          }
          onSave={handleAddMaterial}
          onClose={() => setIsAddModalOpen(false)}
          isSubmitting={isSubmitting}
          fields={ADD_MATERIAL_RENDER_FIELDS}
          labels={ADD_MATERIAL_FORM_LABELS}
        />
      )}

      {filePreviewUrl && (
        <FilePreviewModal
          url={filePreviewUrl}
          onClose={() => setFilePreviewUrl(null)}
        />
      )}
    </div>
  );
};

export default ManageMaterials;
